"use client"
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import HeroBackground from '@/components/ui/hero-background';

const faqData = [
    {
        category: "Getting Started",
        items: [
            { q: "What is Deckwale?", a: "Deckwale is an AI-powered platform that helps founders and startup teams build investor-ready pitch decks in minutes, with zero design or copywriting experience required." },
            { q: "Is Deckwale only for startups?", a: "Primarily yes, but consultants, solo operators, and even agencies use Deckwale for business decks, one-pagers, and client presentations." },
            { q: "Do I need to install anything?", a: "No — Deckwale is 100% browser-based and works on all modern desktop and mobile browsers." },
            { q: "Can I use Deckwale without an account?", a: "To create, save, and export decks, you need to register with your email or Google login." },
            { q: "Does Deckwale support non-English decks?", a: "Yes. While English is the default, our AI supports content generation in multiple languages (beta)." }
        ]
    },
    {
        category: "Deck Creation & Content",
        items: [
            { q: "What types of decks can I create?", a: "You can generate: Pitch Decks (Pre-seed to Series C), One-Pagers, Follow-Up Decks, Investor Updates, Vision & Roadmap Decks, Company Overviews, and Team or Hiring Decks." },
            { q: "What slide formats are supported?", a: "Deckwale follows VC-standard slide flows: Problem, Solution, Market, Business Model, Traction, Team, Ask, etc." },
            { q: "Can I skip AI and write my own slides?", a: "Yes. You can generate a deck and manually overwrite or rearrange any content." },
            { q: "Is there a library of slide templates?", a: "Yes — you can choose from pre-structured deck types and design themes." },
            { q: "Can I duplicate or version a deck?", a: "Pro users can clone decks and view version history to track edits over time." }
        ]
    },
    {
        category: "AI Functionality",
        items: [
            { q: "How does Deckwale’s AI work?", a: "You enter key startup details, and our AI builds a pitch flow based on funding stage and deck type — structuring and writing each slide for clarity and impact." },
            { q: "Can I regenerate individual slides?", a: "Yes — every slide has an “Regenerate” option, offering alternate versions instantly." },
            { q: "Does the AI rewrite slides if content is unclear?", a: "Yes. Our Smart Rewrite feature enhances grammar, conciseness, and pitch tone." },
            { q: "Can I control tone (formal/casual/technical)?", a: "Tone controls are in development and will be part of advanced Pro features soon." },
            { q: "Will the AI suggest what slides to include?", a: "Yes. Based on your funding stage, Deckwale recommends the right flow (e.g., Vision slides for early-stage, Metrics for Series A+)." }
        ]
    },
    {
        category: "Branding & Design",
        items: [
            { q: "Can I add my own logo, colors, and fonts?", a: "Yes — Pro plans support full brand customization via the Smart Brand Kit." },
            { q: "Are the themes editable?", a: "Themes are customizable via pre-designed layouts. Manual theme editing is in roadmap." },
            { q: "Can I lock branding across all decks?", a: "Yes — your brand style is saved per account and applied by default in every deck." },
            { q: "Can I include charts, images, or screenshots?", a: "Yes. Upload and embed media directly into slides or via placeholder prompts." },
            { q: "Is Deckwale mobile responsive?", a: "Yes. Your decks can be viewed on mobile — and mobile-friendly one-pagers are included." }
        ]
    },
    {
        category: "Exporting & Sharing",
        items: [
            { q: "Can I export to PDF or PowerPoint?", a: "Yes. You can export decks as high-quality PDFs or editable .pptx files." },
            { q: "Can I share my deck via a live link?", a: "Yes — live links are available via Seedicon’s Watch tool with access controls." },
            { q: "Is there a watermark on exports?", a: "Free users get a light watermark. Paid users export without branding." },
            { q: "Do shared decks show analytics?", a: "Yes. With Watch links, you can track views, time spent per slide, and more." },
            { q: "Can I track who viewed my deck?", a: "Pro users can enable viewer tracking (email required)." }
        ]
    },
    {
        category: "Pricing & Plans",
        items: [
            { q: "Is Deckwale free to use?", a: "Yes. Our Starter plan is free with limited exports and basic features." },
            { q: "What’s the difference between Free and Pro?", a: "Pro offers: Unlimited decks, PPT export, Branding tools, Rewrite/Regenerate AI, and Priority support." },
            { q: "Is there a plan for accelerators or cohorts?", a: "Yes — Deckwale Teams includes shared templates, dashboards, and admin controls. Contact us for access." },
            { q: "Can I switch plans or cancel anytime?", a: "Yes. You can upgrade, downgrade, or cancel from your billing dashboard." },
            { q: "Do you offer founder/investor discounts?", a: "Yes — we offer discounts to accelerator cohorts and verified early-stage teams. Contact us." }
        ]
    },
    {
        category: "Privacy & Security",
        items: [
            { q: "Is my data encrypted?", a: "Yes. Decks and personal data are encrypted at rest and in transit." },
            { q: "Do you store decks on your servers?", a: "Yes — stored privately and securely in your dashboard. Only you can access them unless shared." },
            { q: "Will you ever sell my data?", a: "Never. We do not sell or monetize user data under any circumstances." },
            { q: "Can I delete my data and account?", a: "Yes. You can delete your account at any time from your settings." },
            { q: "Are you compliant with Indian and U.S. data laws?", a: "Yes. We follow India's DPDP Act (2023), U.S. standards like CCPA/CPRA, and GDPR principles globally." }
        ]
    },
    {
        category: "Watch Tool & Analytics",
        items: [
            { q: "What is Watch by Seedicon?", a: "Watch is a secure data-sharing and investor engagement tracker that lets founders send decks and view detailed analytics." },
            { q: "What does Watch track?", a: "Who viewed your deck, Which slides they spent time on, Drop-off points, and Deck revisit frequency." },
            { q: "Can I password-protect my Watch deck?", a: "Yes. Set access controls, email-gated views, and expiration dates." },
            { q: "Can I share decks with multiple investors?", a: "Yes. Create unique Watch links for each investor if desired." },
            { q: "Can I integrate Watch with CRMs?", a: "CRM sync is in roadmap — currently export links can be manually tracked." }
        ]
    },
    {
        category: "Community & Support",
        items: [
            { q: "Do you offer live support?", a: "Yes. Live chat is available on weekdays. Pro users get 24-hour response time." },
            { q: "Can I request new features?", a: "Yes — use the “Request Feature” link in your dashboard or email support@deckwale.com." },
            { q: "Where can I learn how to pitch better?", a: "Check out our free Pitch Deck Guide, Business Deck Guide, and video tutorials in the Resources section." },
            { q: "Do you run founder webinars or events?", a: "Yes. We host monthly founder workshops, live deck teardowns, and mentorship AMA sessions." },
            { q: "Can I apply as a mentor or partner accelerator?", a: "Yes. Reach out to team@seedicon.com to explore partnerships." }
        ]
    }
];

const FAQs = () => {
    const [activeCategory, setActiveCategory] = useState("Getting Started");
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const activeQuestions = faqData.find(c => c.category === activeCategory)?.items || [];

    return (
        <div className="min-h-screen bg-white relative overflow-hidden font-jakarta text-gray-900 selection:bg-blue-100/50">
            <HeroBackground />

            <div className="relative z-10 pt-32 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-syne font-bold text-4xl md:text-5xl mb-6 tracking-tight"
                    >
                        Frequently Asked Questions
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-gray-500 text-lg md:text-xl font-medium"
                    >
                        Have questions? We're here to help you build better decks.
                    </motion.p>
                </div>

                {/* Category Chips */}
                <div className="flex flex-wrap justify-center gap-3 mb-16 max-w-5xl mx-auto">
                    {faqData.map((section, idx) => (
                        <motion.button
                            key={section.category}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => {
                                setActiveCategory(section.category);
                                setOpenIndex(0); // Reset open accordion on category switch
                            }}
                            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border ${activeCategory === section.category
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-600/20'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            {section.category}
                        </motion.button>
                    ))}
                </div>

                {/* FAQ List (Accordion) */}
                <motion.div
                    key={activeCategory}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-3xl mx-auto space-y-3"
                >
                    {activeQuestions.map((item, idx) => (
                        <div
                            key={idx}
                            className={`group bg-[#f9fcff] backdrop-blur-sm border rounded-xl transition-all duration-200 ${openIndex === idx
                                ? 'border-blue-200 shadow-xl shadow-blue-500/10 bg-white'
                                : 'border-gray-100/50 hover:border-blue-100 hover:shadow-md hover:bg-white'
                                }`}
                        >
                            <button
                                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                                className="w-full text-left px-6 py-4 flex items-start justify-between gap-4 focus:outline-none"
                            >
                                <span className={`font-semibold text-[15px] md:text-[16px] leading-snug transition-colors ${openIndex === idx ? 'text-blue-700' : 'text-slate-800 group-hover:text-blue-600'
                                    }`}>
                                    {item.q}
                                </span>
                                <span className={`mt-0.5 flex-shrink-0 text-blue-500/70 transition-transform duration-300 ${openIndex === idx ? 'rotate-180 text-blue-600' : ''
                                    }`}>
                                    {openIndex === idx ? <Minus size={18} /> : <Plus size={18} />}
                                </span>
                            </button>

                            <AnimatePresence>
                                {openIndex === idx && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-6 pb-5 pt-0 text-slate-600 text-[14px] leading-relaxed">
                                            {item.a}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default FAQs;
